import { Box, Chip, Stack, useTheme } from '@mui/joy';
import { CellDoubleClickedEvent, ICellRendererParams } from 'ag-grid-community';
import { useEffect, useState } from 'react';

import { ElementType } from 'bom/enums';
import { IElement } from 'bom/types';
import { TextEllipsis } from 'core/elements';
import { NumberHelper } from 'core/helpers';
import { getAgGridTableCellData } from 'core/utils';
import { ManageElementsButton } from 'jobs/components/JobDetails';
import { IJobPart } from 'jobs/types';
import { CustomColor, Direction, Variant } from 'theme/enums';

interface IProps extends ICellRendererParams {
  isEditable: (data: IJobPart) => boolean;
}

export const ElementsCell = ({ isEditable, ...props }: IProps) => {
  const [isOpenManageElements, setIsOpenManageElements] = useState(false);

  const theme = useTheme();

  const data = getAgGridTableCellData(props, 'elements');

  const isRowEditable = isEditable(data);

  useEffect(() => {
    const handleDoubleClick = (eventParams: CellDoubleClickedEvent) => {
      if (
        eventParams.node.rowIndex === props.node.rowIndex &&
        eventParams.column.getColId() === props.column.getColId()
      ) {
        setIsOpenManageElements(true);
      }
    };

    props.api.addEventListener('cellDoubleClicked', handleDoubleClick);

    return () => {
      !props.api.isDestroyed() &&
        props.api.removeEventListener('cellDoubleClicked', handleDoubleClick);
    };
  }, [props.api, props.column, props.node.rowIndex]);

  if (!data) {
    return null;
  }

  const sortedElements = (data.elements || []).sort((a: IElement, b: IElement) => {
    // No Element Assigned (empty=1) first, then regular elements, then overage/shortage last
    const aIsOverageOrShortage =
      a.id === ElementType.ID_OVERAGE || a.id === ElementType.ID_SHORTAGE;
    const bIsOverageOrShortage =
      b.id === ElementType.ID_OVERAGE || b.id === ElementType.ID_SHORTAGE;

    if (a.empty === 1 && b.empty !== 1) {
      return -1;
    }

    if (a.empty !== 1 && b.empty === 1) {
      return 1;
    }

    if (aIsOverageOrShortage && !bIsOverageOrShortage) {
      return 1;
    }

    if (!aIsOverageOrShortage && bIsOverageOrShortage) {
      return -1;
    }

    return 0;
  });

  const quantitiesPerElement = data.quantitiesPerElement || {};

  return (
    <Stack spacing={1}>
      <TextEllipsis
        content={
          <Stack direction={Direction.ROW} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {sortedElements.map((element: IElement) => {
              const quantity = element.quantity * (quantitiesPerElement?.[element.id] || 1);
              const isOverageOrShortage = [
                ElementType.ID_OVERAGE,
                ElementType.ID_SHORTAGE,
              ].includes(element.id as ElementType);

              return (
                <Chip
                  key={element.id}
                  variant={Variant.SOLID}
                  sx={{
                    borderRadius: '24px',
                    fontSize: '12px',
                    ...(isOverageOrShortage && {
                      backgroundColor: theme.palette.neutral[100],
                      color: theme.palette.common[CustomColor.BLACK],
                    }),
                  }}
                >
                  <Box component="span">{element.name}</Box>
                  <Box
                    component="span"
                    sx={{
                      backgroundColor: theme.palette.neutral[600],
                      color: isOverageOrShortage
                        ? theme.palette.common[CustomColor.WHITE]
                        : 'inherit',
                      fontWeight: 700,
                      marginLeft: '4px',
                      padding: '2px 8px',
                      width: 'fit-content',
                      borderRadius: '16px',
                      textAlign: 'center',
                    }}
                  >
                    {NumberHelper.format({ amount: quantity, decimals: 0 })}
                  </Box>
                </Chip>
              );
            })}
          </Stack>
        }
        lineHeight={32}
        maxRows={3}
        showMoreLabel
      />

      {isRowEditable && (
        <Box display={'flex'} justifyContent={'flex-end'}>
          <ManageElementsButton
            bomPart={data}
            isOpenExternal={isOpenManageElements}
            updateIsOpenExternal={setIsOpenManageElements}
          />
        </Box>
      )}
    </Stack>
  );
};
